/** Valore mostrato al centro / tooltip: allineato a Formatter (EUR, 2 decimali) quando format === 'currency'. */
const formatPieValue = (format, locale, val) => {
    const n = typeof val === 'number' ? val : parseFloat(val, 10);
    if (format !== 'currency' || Number.isNaN(n)) {
        return val;
    }
    try {
        return new Intl.NumberFormat(locale || undefined, {
            style: 'currency',
            currency: 'EUR',
            maximumFractionDigits: 2,
        }).format(n);
    } catch {
        return String(val);
    }
};

export const translatePieLabel = (t, value) => {
    if (value == null) return '';
    const raw = String(value);
    const trimmed = raw.trim();
    if (trimmed === '') return raw;

    const candidates = [
        trimmed,
        trimmed.toLowerCase(),
        `status.${trimmed}`,
        `status.${trimmed.toLowerCase()}`,
    ];

    for (const key of candidates) {
        const translated = t(key);
        if (translated !== key) return translated;
    }

    return raw;
};

/** Palette Apex (allineate a theme.palette) per legenda HTML esterna. */
const APEX_PALETTE_COLORS = {
    palette1: ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0', '#546E7A', '#26a69a', '#D10CE8'],
    palette2: ['#3f51b5', '#03a9f4', '#4caf50', '#f9ce1d', '#FF9800', '#33b2df', '#546E7A', '#d4526e'],
    palette3: ['#90ee7e', '#f48024', '#69d2e7', '#d4526e', '#13d8aa', '#A5978B', '#2b908f', '#f9a3a4'],
    palette4: ['#662E9B', '#F86624', '#F9C80E', '#EA3546', '#43BCCD', '#5C4742', '#8D5B4C', '#5A2A27'],
};

export const resolvePieSliceColors = (labels, { labelToColorMap, palette, monochrome, hasData }) => {
    if (!Array.isArray(labels) || labels.length === 0) {
        return ['#CCCCCC'];
    }

    if (!hasData) {
        return ['#CCCCCC'];
    }

    const paletteKey = palette || 'palette1';
    const fallbackPalette = APEX_PALETTE_COLORS[paletteKey] || APEX_PALETTE_COLORS.palette1;

    if (monochrome?.enabled) {
        const color = monochrome.color || '#CCCCCC';
        return labels.map(() => color);
    }

    return labels.map((label, index) => {
        if (labelToColorMap) {
            const mapped = labelToColorMap[String(label).toLowerCase()];
            if (mapped) return mapped;
        }
        return fallbackPalette[index % fallbackPalette.length];
    });
};


/** Altezza totale chartwrapper in stampa (donut + legenda fissa sotto). */
export const PRINT_PIE_CHART_HEIGHT = 320;

/** Spazio riservato alla legenda quando è in basso/alto: uguale per tutti i Pie. */
export const PIE_LEGEND_HEIGHT_BOTTOM = 80;

/** Larghezza riservata alla legenda quando è a sx/dx (se non passata via prop). */
export const PIE_LEGEND_WIDTH_SIDE_DEFAULT = 160;

export const isLegendAtBottom = (position) => position === 'bottom' || position === 'top';

/**
 * Calcola dimensioni Apex dal container: il grafo occupa sempre lo spazio rimanente
 * dopo aver riservato un box fisso alla legenda (altezza se in basso, larghezza se a lato).
 */
export const resolvePieLayout = ({
    containerWidth,
    containerHeight,
    legendPosition = 'right',
    legendWidth: legendWidthProp,
    showLegend = true,
    printMode = false,
}) => {
    const effectivePosition = printMode ? 'bottom' : legendPosition;
    const apexWidth = Math.max(Math.floor(containerWidth), 1);
    const apexHeight = Math.max(Math.floor(containerHeight), 1);

    if (!showLegend) {
        return {
            apexWidth,
            apexHeight,
            graphWidth: apexWidth,
            graphHeight: apexHeight,
            legendWidth: 0,
            legendHeight: 0,
            legendPosition: effectivePosition,
        };
    }

    if (isLegendAtBottom(effectivePosition)) {
        const legendHeight = PIE_LEGEND_HEIGHT_BOTTOM;
        return {
            apexWidth,
            apexHeight,
            graphWidth: apexWidth,
            graphHeight: Math.max(apexHeight - legendHeight, 1),
            legendWidth: apexWidth,
            legendHeight,
            legendPosition: effectivePosition,
        };
    }

    const legendWidth = typeof legendWidthProp === 'number'
        ? legendWidthProp
        : PIE_LEGEND_WIDTH_SIDE_DEFAULT;

    return {
        apexWidth,
        apexHeight,
        graphWidth: Math.max(apexWidth - legendWidth, 1),
        graphHeight: apexHeight,
        legendWidth,
        legendHeight: apexHeight,
        legendPosition: effectivePosition,
    };
};

/** Ritorna { series, options } o null. Un solo oggetto derivato evita doppio setState e doppio update Apex (flicker tooltip). */
export const buildPieChartConfig = (items, monochrome, showLegend, layout, mode, palette, showTotal, showName, showValues, labelToColorMap, t, handleFilterChange, format, locale, showSliceTooltip, printMode = false) => {
    if (!(items && typeof items === 'object' && !Array.isArray(items))) {
        return null;
    }

    const total = Array.isArray(items.series)
        ? items.series.reduce((a, b) => a + b, 0)
        : 0;

    const hasData = total > 0;
    const monochromel = hasData ? monochrome : {
        enabled: true,
        color: '#CCCCCC',
        shadeTo: 'dark',
        shadeIntensity: 1
    };

    const series = hasData ? items.series : [1];

    const effectiveShowTotal = printMode ? true : showTotal;
    const effectiveShowName = printMode ? false : showName;
    const effectiveShowValues = printMode ? false : showValues;
    const effectiveShowSliceTooltip = printMode ? false : showSliceTooltip !== false;
    const effectiveLegendPosition = layout.legendPosition;
    const legendAtBottom = isLegendAtBottom(effectiveLegendPosition);
    const useExternalLegend = showLegend && legendAtBottom;
    const chartHeight = useExternalLegend ? layout.graphHeight : layout.apexHeight;
    const chartWidth = printMode ? '100%' : layout.apexWidth;

    const options = {
            labels: hasData ? items.labels : [t('Nessun dato')],
            legend: {
                show: showLegend && !useExternalLegend,
                position: effectiveLegendPosition,
                width: showLegend ? layout.legendWidth : undefined,
                height: showLegend && legendAtBottom ? layout.legendHeight : undefined,
                maxHeight: showLegend && legendAtBottom ? layout.legendHeight : undefined,
                offsetY: legendAtBottom ? 0 : 0,
                floating: false,
                horizontalAlign: legendAtBottom ? 'left' : undefined,
                containerMargin: legendAtBottom
                    ? { left: 0, top: 0 }
                    : {
                    left: 5,
                    top: 5
                },
                itemMargin: {
                    horizontal: 5,
                    vertical: 2
                },
                markers: {
                    width: 12,
                    height: 12,
                    radius: 2
                },
                labels: {
                    useSeriesColors: true
                },
                formatter: function (seriesName, opts) {
                    return translatePieLabel(t, seriesName);
                },
                onItemClick: {
                    toggleDataSeries: false
                }
            },
            theme: {
                mode: mode,
                palette: palette,
                monochrome: monochromel
            },
            chart: {
                type: 'donut',
                width: chartWidth,
                height: chartHeight,
                animations: {
                    enabled: false,
                    dynamicAnimation: { enabled: false },
                },
                events: {
                    dataPointSelection: (event, chartContext, config) => {
                        const clickedSliceIndex = config.dataPointIndex;
                        const clickedLabel = config.w.config.labels[clickedSliceIndex];
                        if (handleFilterChange && clickedSliceIndex < 10 && hasData) {
                            setTimeout(() => handleFilterChange(clickedLabel), 100)
                        }
                    }
                }
            },
            responsive: printMode
                ? []
                : [
                {
                    breakpoint: 600,
                    options: {
                        chart: {
                            width: Math.floor(layout.apexWidth * 0.75),
                            height: Math.floor(layout.apexHeight * 0.75),
                        },
                        plotOptions: {
                            pie: {
                                expandOnClick: false,
                                donut: {
                                    labels: {
                                        show: true,
                                        total: {
                                            show: false,
                                        }
                                    }
                                }
                            }
                        },
                        legend: {
                            position: 'bottom',
                            width: undefined,
                            height: undefined
                        }
                    }
                },
                {
                    breakpoint: 0,
                    options: {
                        chart: {
                            width: layout.apexWidth,
                            height: layout.apexHeight,
                        }
                    }
                }
            ],
            dataLabels: {
                enabled: true,
                enabledOnSeries: undefined,
                formatter: function (val, opts) {
                    if (printMode) {
                        const rawValue = opts.w.globals.series[opts.seriesIndex];
                        if (format === 'currency') {
                            return formatPieValue(format, locale, rawValue);
                        }
                        const numericValue = typeof rawValue === 'number' ? rawValue : parseFloat(rawValue, 10);
                        if (Number.isNaN(numericValue)) {
                            return String(rawValue);
                        }
                        return Number.isInteger(numericValue)
                            ? String(numericValue)
                            : numericValue.toLocaleString(locale || undefined, { maximumFractionDigits: 1 });
                    }
                    return `${parseFloat(val).toFixed(1)}%`;
                },
                textAnchor: 'middle',
                distributed: false,
                offsetX: 0,
                offsetY: 0,
                style: {
                    fontSize: printMode ? '12px' : '10px',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 'bold',
                    colors: ['#FFF'],
                    cursor: 'pointer',
                },
                dropShadow: {
                    enabled: false,
                }
            },
            plotOptions: {
                pie: {
                    expandOnClick: false,
                    states: {
                        hover: { filter: { type: 'none' } },
                        active: { filter: { type: 'none' } },
                    },
                    donut: {
                        size: '50%',
                        labels: {
                            show: true,
                            total: {
                                show: effectiveShowTotal,
                                fontSize: printMode ? '20px' : '12px',
                                fontFamily: 'Helvetica, Arial, sans-serif',
                                label: printMode ? '' : t('Total'),
                                fontWeight: printMode ? 700 : 200,
                                offsetY: printMode ? 0 : (layout.graphWidth <= 200 ? -20 : -15),
                                color: undefined,
                                formatter: function () {
                                    if (!hasData) {
                                        return format === 'currency' ? formatPieValue(format, locale, 0) : '0';
                                    }
                                    return formatPieValue(format, locale, total);
                                }
                            },
                            name: {
                                show: effectiveShowName,
                                fontSize: '12px',
                                fontFamily: 'Helvetica, Arial, sans-serif',
                                label: t('Total'),
                                fontWeight: 200,
                                offsetY: layout.graphWidth <= 200 ? -20 : -15,
                                color: undefined,
                                formatter: function (value) {
                                    return translatePieLabel(t, value);
                                }
                            },
                            value: {
                                show: effectiveShowValues,
                                fontSize: layout.graphWidth <= 200 ? '30px' : '24px',
                                fontFamily: 'Helvetica, Arial, sans-serif',
                                fontWeight: 600,
                                offsetY: -5,
                                formatter: function (value) {
                                    if (!hasData) return '';
                                    return formatPieValue(format, locale, value);
                                }
                            },
                        }
                    }
                }
            },
            tooltip: {
                enabled: effectiveShowSliceTooltip,
                theme: 'light',
                shared: false,
                followCursor: false,
                intersect: false,
                x: {
                    formatter: (value) => translatePieLabel(t, value),
                },
                y: {
                    formatter: (value) => formatPieValue(format, locale, value),
                },
            }
        };

    const sliceColors = resolvePieSliceColors(hasData ? items.labels : [t('Nessun dato')], {
        labelToColorMap,
        palette,
        monochrome: monochromel,
        hasData,
    });
    options.colors = sliceColors;

    return { series, options, useExternalLegend, chartHeight, sliceColors };
}

export const calculateWidths = (width, legendWidth, donutWidth) => {
    // PALETTO: Lavoriamo SOLO con numeri interi (pixel)
    // Se vuoi percentuali, devi convertirle prima di chiamare questa funzione
    
    const hasWidth = typeof width === 'number';
    const hasLegend = typeof legendWidth === 'number';
    const hasDonut = typeof donutWidth === 'number';

    // Caso 1: solo width
    if (hasWidth && !hasLegend && !hasDonut) {
        return {
            donut: Math.floor(width * 0.7),
            legend: Math.floor(width * 0.3)
        };
    }

    // Caso 2: solo legend
    if (!hasWidth && hasLegend && !hasDonut) {
        const totalWidth = Math.floor(legendWidth / 0.3);
        return {
            donut: Math.floor(totalWidth * 0.7),
            legend: legendWidth
        };
    }

    // Caso 3: solo donut
    if (!hasWidth && !hasLegend && hasDonut) {
        const totalWidth = Math.floor(donutWidth / 0.7);
        return {
            donut: donutWidth,
            legend: Math.floor(totalWidth * 0.3)
        };
    }

    // Caso 4: width + legend
    if (hasWidth && hasLegend && !hasDonut) {
        return {
            donut: width - legendWidth,
            legend: legendWidth
        };
    }

    // Caso 5: width + donut
    if (hasWidth && !hasLegend && hasDonut) {
        return {
            donut: donutWidth,
            legend: width - donutWidth
        };
    }

    // Caso 6: legend + donut (senza width)
    if (!hasWidth && hasLegend && hasDonut) {
        return {
            donut: donutWidth,
            legend: legendWidth
        };
    }

    // Caso 7: tutti e tre - verifica coerenza
    if (hasWidth && hasLegend && hasDonut) {
        if (Math.abs((donutWidth + legendWidth) - width) > 1) {
            console.warn(`Width mismatch: donut(${donutWidth}) + legend(${legendWidth}) ≠ total(${width})`);
        }
        return { donut: donutWidth, legend: legendWidth };
    }

    // Default fallback
    return {
        donut: 280,
        legend: 120
    };
};

export const getStyles = (layout, showLegend = true, useExternalLegend = false) => {
    const base = {
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: useExternalLegend ? 'visible' : 'hidden',
    };

    if (!showLegend || !layout || useExternalLegend) {
        return base;
    }

    if (isLegendAtBottom(layout.legendPosition)) {
        return {
            ...base,
            '& .apexcharts-legend': {
                minHeight: `${layout.legendHeight}px !important`,
                maxHeight: `${layout.legendHeight}px !important`,
                height: `${layout.legendHeight}px !important`,
                overflowY: 'auto !important',
                overflowX: 'hidden !important',
                padding: '4px 0 0 !important',
                justifyContent: 'flex-start !important',
                alignContent: 'flex-start !important',
            },
            '& .apexcharts-legend-series': {
                display: 'inline-flex !important',
                alignItems: 'flex-start !important',
                margin: '0 12px 4px 0 !important',
            },
            '& .apexcharts-legend-text': {
                whiteSpace: 'normal !important',
                wordWrap: 'break-word !important',
                lineHeight: '1.35 !important',
                fontSize: '11px !important',
                maxWidth: '140px !important',
                display: 'inline-block !important',
                textAlign: 'left !important',
            },
        };
    }

    return {
        ...base,
        '& .apexcharts-legend': {
            minWidth: `${layout.legendWidth}px !important`,
            maxWidth: `${layout.legendWidth}px !important`,
            width: `${layout.legendWidth}px !important`,
            overflowY: 'auto !important',
            overflowX: 'hidden !important',
            padding: '0 5px !important',
            alignContent: 'flex-start !important',
        },
        '& .apexcharts-legend-series': {
            display: 'flex !important',
            alignItems: 'flex-start !important',
            marginBottom: '4px !important',
        },
        '& .apexcharts-legend-text': {
            whiteSpace: 'normal !important',
            wordWrap: 'break-word !important',
            lineHeight: '1.4 !important',
            maxWidth: `${Math.max(layout.legendWidth - 24, 80)}px !important`,
            display: 'inline-block !important',
            textAlign: 'left !important',
        },
    };
};